import React, { useState, useEffect, useCallback } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiClock, FiAlertTriangle, FiCheckCircle, FiTrendingUp } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDate = (iso) => {
  if (!iso) return '—';
  const p = String(iso).split('-');
  if (p.length !== 3) return iso;
  return `${p[2].padStart(2,'0')}-${MONTHS_SHORT[parseInt(p[1],10)-1]}-${p[0]}`;
};

const BUCKET_COLORS = {
  '0':      { bar: '#10B981', label: 'tw-text-emerald-600' },
  '1-7':    { bar: '#3B82F6', label: 'tw-text-blue-600' },
  '8-30':   { bar: '#F59E0B', label: 'tw-text-amber-600' },
  '31-90':  { bar: '#F97316', label: 'tw-text-orange-600' },
  '91+':    { bar: '#EF4444', label: 'tw-text-red-600' },
};

const DelayBadge = ({ days }) => {
  if (days === 0)  return <span className="tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-bg-emerald-100 tw-text-emerald-700">Same day</span>;
  if (days <= 7)   return <span className="tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-bg-blue-100 tw-text-blue-700">{days}d</span>;
  if (days <= 30)  return <span className="tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-bg-amber-100 tw-text-amber-700">{days}d</span>;
  if (days <= 90)  return <span className="tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-bg-orange-100 tw-text-orange-700">{days}d</span>;
  return <span className="tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-bg-red-100 tw-text-red-700">{days}d</span>;
};
DelayBadge.propTypes = { days: PropTypes.number };

const TypeBadge = ({ type }) => type === 'contract'
  ? <span className="tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-xs tw-font-bold tw-bg-blue-100 tw-text-blue-700">Contr.</span>
  : <span className="tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-xs tw-font-bold tw-bg-amber-100 tw-text-amber-700">Adden.</span>;
TypeBadge.propTypes = { type: PropTypes.string };

const ContractReleaseDelayCard = ({ baseParams }) => {
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [showAll,     setShowAll]     = useState(false);
  const [viewMode,    setViewMode]    = useState('handler'); // 'records' | 'handler'

  const fetchData = useCallback(() => {
    if (!baseParams) return;
    setLoading(true);
    apiAxios.get('/crm/dashboard/contracts/get_release_delay_data', { params: baseParams })
      .then(res  => setData(res.data?.data || null))
      .catch(()  => setData(null))
      .finally(() => setLoading(false));
  }, [baseParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <Card className="tw-border tw-border-gray-200 tw-shadow-sm">
      <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
        <FiClock size={15} className="tw-text-orange-500" />
        <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm">Release Delay Analysis</h3>
      </div>
      <div className="tw-flex tw-justify-center tw-py-8"><LoadingSpinner size="md" /></div>
    </Card>
  );

  if (!data) return null;

  const { summary, distribution, by_handler, cross_month_records } = data;
  const totalReleased   = (summary.total_contracts || 0) + (summary.total_addendums || 0);
  const totalCrossMonth = (summary.cross_month_contracts || 0) + (summary.cross_month_addendums || 0);
  const pctDelayed      = totalReleased > 0 ? ((totalCrossMonth / totalReleased) * 100).toFixed(1) : '0.0';
  const avgDelay        = totalReleased > 0
    ? (((summary.avg_delay_contracts || 0) * (summary.total_contracts || 0) +
        (summary.avg_delay_addendums || 0) * (summary.total_addendums || 0)) /
       Math.max(totalReleased, 1)).toFixed(1)
    : '0';

  const maxDistCount = Math.max(...(distribution || []).map(d => d.count), 1);
  const records      = cross_month_records || [];
  const visibleRecs  = showAll ? records : records.slice(0, 10);

  if (totalReleased === 0) return null;

  return (
    <Card className="tw-border tw-border-gray-200 tw-shadow-sm">
      {/* Header */}
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiClock size={15} className="tw-text-orange-500" />
          <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm">Release Delay Analysis</h3>
          <span className="tw-text-xs tw-text-gray-400">Cross-month release tracking</span>
        </div>
        <div className="tw-flex tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
          {[['records', 'Records'], ['handler', 'By Handler']].map(([v, l]) => (
            <button key={v} onClick={() => setViewMode(v)}
              className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-transition-colors tw-cursor-pointer ${viewMode === v ? 'tw-bg-orange-500 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-0 tw-border-b tw-border-gray-100">
        {/* Row 1 */}
        <div className="tw-px-5 tw-py-4 tw-border-r tw-border-b tw-border-gray-100">
          <p className="tw-text-xs tw-text-gray-400 tw-mb-1">Total Released</p>
          <p className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-leading-none tw-mb-1">{totalReleased.toLocaleString()}</p>
          <p className="tw-text-xs tw-text-gray-400">{summary.total_contracts}C + {summary.total_addendums}A</p>
        </div>
        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
          <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
            <FiCheckCircle size={11} className="tw-text-emerald-500" />
            <p className="tw-text-xs tw-text-gray-400">On-time (same month)</p>
          </div>
          <p className="tw-text-3xl tw-font-bold tw-text-emerald-600 tw-leading-none tw-mb-1">{(totalReleased - totalCrossMonth).toLocaleString()}</p>
          <p className="tw-text-xs tw-text-emerald-500">{(100 - parseFloat(pctDelayed)).toFixed(1)}% of total</p>
        </div>
        {/* Row 2 */}
        <div className="tw-px-5 tw-py-4 tw-border-r tw-border-gray-100">
          <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
            <FiAlertTriangle size={11} className="tw-text-orange-500" />
            <p className="tw-text-xs tw-text-gray-400">Cross-month</p>
          </div>
          <p className="tw-text-3xl tw-font-bold tw-text-orange-600 tw-leading-none tw-mb-1">{totalCrossMonth.toLocaleString()}</p>
          <p className="tw-text-xs tw-text-orange-500">{pctDelayed}% of total</p>
        </div>
        <div className="tw-px-5 tw-py-4">
          <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
            <FiTrendingUp size={11} className="tw-text-blue-500" />
            <p className="tw-text-xs tw-text-gray-400">Avg delay (all)</p>
          </div>
          <p className="tw-text-3xl tw-font-bold tw-text-blue-600 tw-leading-none tw-mb-1">{avgDelay}d</p>
          <p className="tw-text-xs tw-text-gray-400">Max: {Math.max(summary.max_delay_contracts || 0, summary.max_delay_addendums || 0)}d</p>
        </div>
      </div>

      <div className="tw-p-5">
        {/* Delay distribution — always visible */}
        <div className="tw-mb-5">
          <p className="tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-widest tw-mb-3">Delay Distribution (all released)</p>
          <div className="tw-space-y-2.5">
            {(distribution || []).map((d) => {
              const cfg   = BUCKET_COLORS[d.bucket] || { bar: '#6B7280', label: 'tw-text-gray-600' };
              const width = d.count > 0 ? Math.max((d.count / maxDistCount) * 100, 1.5) : 0;
              return (
                <div key={d.bucket} className="tw-flex tw-items-center tw-gap-3">
                  <span className="tw-text-xs tw-text-gray-500 tw-w-16 tw-shrink-0">{d.label}</span>
                  <div className="tw-flex-1 tw-bg-gray-100 tw-rounded-xl tw-h-4 tw-overflow-hidden">
                    <div
                      className="tw-h-full tw-rounded-xl tw-transition-all tw-duration-500"
                      style={{ width: `${width}%`, backgroundColor: cfg.bar }}
                    />
                  </div>
                  <span className={`tw-text-xs tw-font-bold tw-w-8 tw-text-right ${cfg.label}`}>{d.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom table — toggled by viewMode */}
        {viewMode === 'handler' ? (
          /* By Handler view */
          <div>
            <p className="tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-widest tw-mb-3">Cross-month by Handler</p>
            {(by_handler || []).length === 0 ? (
              <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-4">No cross-month releases</p>
            ) : (
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-xs tw-border-collapse">
                  <thead>
                    <tr className="tw-bg-gray-50">
                      <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100">Handler</th>
                      <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-blue-600 tw-border-b tw-border-gray-100">Contracts</th>
                      <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-amber-600 tw-border-b tw-border-gray-100">Addendums</th>
                      <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-100">Total</th>
                      <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-orange-600 tw-border-b tw-border-gray-100">Avg delay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(by_handler || []).map((r, i) => (
                      <tr key={i} className="hover:tw-bg-slate-50 tw-transition-colors">
                        <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50">
                          <div className="tw-flex tw-items-center tw-gap-1.5">
                            <span className="tw-w-5 tw-h-5 tw-rounded-xl tw-bg-orange-100 tw-text-orange-700 tw-text-xs tw-font-bold tw-flex tw-items-center tw-justify-center tw-shrink-0">
                              {(r.released_by || '?')[0].toUpperCase()}
                            </span>
                            <span className="tw-font-medium tw-text-gray-800">{r.released_by || '—'}</span>
                          </div>
                        </td>
                        <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center tw-font-semibold tw-text-blue-600">{r.contracts}</td>
                        <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center tw-font-semibold tw-text-amber-600">{r.addendums}</td>
                        <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center tw-font-bold tw-text-gray-900">{(r.contracts || 0) + (r.addendums || 0)}</td>
                        <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center">
                          <DelayBadge days={r.avg_delay || 0} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* Records view */
          <div>
            <p className="tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-widest tw-mb-3">
              Cross-month Records
              {records.length > 0 && <span className="tw-ml-1.5 tw-normal-case tw-text-orange-500 tw-font-semibold">({records.length})</span>}
            </p>
            {records.length === 0 ? (
              <div className="tw-flex tw-items-center tw-gap-2 tw-justify-center tw-py-6 tw-text-emerald-600">
                <FiCheckCircle size={16} />
                <p className="tw-text-sm tw-font-semibold">All releases are on-time (same month)</p>
              </div>
            ) : (
              <>
                <div className="tw-overflow-x-auto">
                  <table className="tw-w-full tw-text-xs tw-border-collapse">
                    <thead>
                      <tr className="tw-bg-gray-50">
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100">J Number</th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100">Type</th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100 tw-hidden md:tw-table-cell">Project</th>
                        <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100 tw-hidden md:tw-table-cell">Signed Date</th>
                        <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100" title="Contract date for contracts, addendum date for addendums — delay is measured from this date">Contr./Adden. Date</th>
                        <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100">Released Date</th>
                        <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-orange-600 tw-border-b tw-border-gray-100">Delay</th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100">Released By</th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100 tw-hidden md:tw-table-cell">Region</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRecs.map((r, i) => (
                        <tr key={i} className="hover:tw-bg-orange-50/40 tw-transition-colors">
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50">
                            {r.deal_id ? (
                              <Link to={`/crm/projects/${r.deal_id}`} target="_blank" rel="noreferrer"
                                className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline">
                                {r.j_number || '—'}
                              </Link>
                            ) : <span className="tw-font-mono tw-font-bold tw-text-gray-700">{r.j_number || '—'}</span>}
                          </td>
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50">
                            <TypeBadge type={r.record_type} />
                          </td>
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-gray-700 tw-max-w-[180px] tw-truncate tw-hidden md:tw-table-cell" title={r.project_name}>
                            {r.project_name || '—'}
                          </td>
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center tw-text-gray-600 tw-hidden md:tw-table-cell">{fmtDate(r.signed_date)}</td>
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center tw-text-gray-600">{fmtDate(r.source_date)}</td>
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center tw-font-semibold tw-text-orange-600">{fmtDate(r.released_date)}</td>
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center">
                            <DelayBadge days={r.delay_days} />
                          </td>
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-gray-700 tw-font-medium">{r.released_by || '—'}</td>
                          <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-gray-500 tw-hidden md:tw-table-cell">{r.region || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {records.length > 10 && (
                  <button
                    onClick={() => setShowAll(v => !v)}
                    className="tw-mt-3 tw-w-full tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-font-semibold tw-py-1.5 tw-border tw-border-dashed tw-border-blue-200 tw-rounded tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors"
                  >
                    {showAll ? 'Show less ↑' : `Show all ${records.length} records ↓`}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

ContractReleaseDelayCard.propTypes = {
  baseParams: PropTypes.object
};
ContractReleaseDelayCard.defaultProps = { baseParams: null };

export default ContractReleaseDelayCard;
